import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { Libraries, useJsApiLoader } from '@react-google-maps/api';
import {
    createContext,
    memo,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import { When } from 'react-if';
import { boundsToString } from '../utils';
import throttle from 'lodash.throttle';

type MapContextType = {
    bounds: string | undefined;
    setBounds: (bounds: string | undefined) => void;
    fitBounds: (bounds: google.maps.LatLngBounds, padding?: number) => void;
    clearBounds: () => void;
    map: google.maps.Map | null;
    setMap: (map: google.maps.Map | null) => void;
    markers: Set<google.maps.Marker>;
    setMarkers: (markers: Set<google.maps.Marker>) => void;
    clusterer: MarkerClusterer | undefined;
    setClusterer: (clusterer: MarkerClusterer | undefined) => void;
    isGMScriptLoaded: boolean;
    setIsGMScriptLoaded: (isReady: boolean) => void;
    apiKey: string | undefined;
    setApiKey: (apiKey: string | undefined) => void;
    isInitialized: boolean; // api key is set, scripts are loaded, map exists
};

const MAP_LIBRARIES: Libraries = ['places', 'visualization'];
const MAP_VERSION = 'weekly';
export const DEFAULT_MAP_ID = '5df43df3eca99b1c';

const MapContext = createContext<MapContextType | undefined>(undefined);

export const MapProvider = ({ children }: { children: React.ReactNode }) => {
    const [bounds, setBounds] = useState<string | undefined>(undefined);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [markers, setMarkers] = useState<Set<google.maps.Marker>>(new Set());
    const [clusterer, setClusterer] = useState<MarkerClusterer | undefined>(
        undefined
    );
    const [isGMScriptLoaded, setIsGMScriptLoaded] = useState<boolean>(false);
    const [apiKey, setApiKey] = useState<string | undefined>(undefined);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // notify when the map is ready
    useEffect(() => {
        if (!isInitialized) {
            setIsInitialized(!!apiKey && isGMScriptLoaded && !!map);
        }
    }, [apiKey, isGMScriptLoaded, map]);

    // register a listener to keep the map bounds updated
    useEffect(() => {
        if (isInitialized) {
            const listener = map?.addListener(
                'bounds_changed',
                throttle(
                    () => {
                        if (map.getBounds()) {
                            setBounds(boundsToString(map.getBounds()!));
                        }
                    },
                    500,
                    { trailing: true, leading: false }
                )
            );
            return () => {
                if (listener) {
                    listener.remove();
                }
            };
        }
        return;
    }, [isInitialized]);

    const fitBounds = useCallback(
        (bounds: google.maps.LatLngBounds, padding?: number) => {
            if (map) {
                map.fitBounds(bounds, padding);
            }
        },
        [map]
    );

    return (
        <MapContext.Provider
            value={{
                bounds,
                setBounds,
                fitBounds,
                clearBounds: () => setBounds(undefined),
                map,
                setMap,
                markers,
                setMarkers,
                clusterer,
                setClusterer,
                isGMScriptLoaded,
                setIsGMScriptLoaded,
                apiKey,
                setApiKey: (key: string | undefined) => {
                    setApiKey((prev) => {
                        // API Key cannot be changed or unset
                        return prev?.length ? prev : key;
                    });
                },
                isInitialized,
            }}
        >
            <When condition={!!apiKey}>
                <ScriptLoader
                    apiKey={apiKey}
                    setIsLoaded={setIsGMScriptLoaded}
                />
            </When>
            {children}
        </MapContext.Provider>
    );
};

// limit rerenders
const ScriptLoader = memo(
    ({
        apiKey,
        setIsLoaded,
    }: {
        apiKey: MapContextType['apiKey'];
        setIsLoaded: MapContextType['setIsGMScriptLoaded'];
    }): null => {
        const { isLoaded } = useJsApiLoader({
            id: 'google-maps',
            googleMapsApiKey: apiKey!,
            version: MAP_VERSION,
            libraries: MAP_LIBRARIES,
        });

        useEffect(() => setIsLoaded(isLoaded), [isLoaded]);

        return null;
    },
    (props) => !!props?.apiKey?.length && !!props?.setIsLoaded
);

export const useMap = () => {
    const context = useContext(MapContext);
    if (context === undefined) {
        throw new Error('useMap must be used within a MapProvider');
    }
    return context;
};
